import { Signal } from "@suse-o11y/signal";
import React from "react";

import { type VariantProp, cv } from "~/tools/class-variance";
import { ImmutableMap } from "~/tools/immutable-map";
import { ImmutableSet } from "~/tools/immutable-set";
import { isShallowEqual } from "~/tools/is-shallow-equal";
import { useOn } from "~/tools/use-on";
import { useOnResize } from "~/tools/use-on-resize";
import { usePermanent } from "~/tools/use-permanent";

import { TableContextProvider, type TableContextValue } from "./_context";
import { type TableOverflowState, readTableOverflowState } from "./_overflow-state";
import { TableScrollIndicator } from "./_scroll-indicator";
import styles from "./_table.module.css";

const variants = cv(styles.table, {
	variants: {
		theme: {
			"base-100": styles.themeBase100,
			"base-200": styles.themeBase200,
		},

		text: {
			xs: styles.textXs,
			sm: styles.textSm,
		},

		"border[row-b]": {
			true: styles.borderRowB,
		},

		"border[row-t]": {
			true: styles.borderRowT,
		},

		"padding[cell-xs]": {
			true: styles.paddingCellXs,
		},

		"padding[cell-y-md]": {
			true: styles.paddingCellYMd,
		},

		"padding[cell-y-sm]": {
			true: styles.paddingCellYSm,
		},

		"padding[head-cell-y-md]": {
			true: styles.paddingHeadCellYMd,
		},
	},

	defaultVariants: {
		theme: "base-200",
		text: "sm",
	},
});

export type TableTheme = VariantProp<typeof variants, "theme">;

export type TableText = VariantProp<typeof variants, "text">;

export type TableBorder = "row-b" | "row-t";

export type TablePadding = "cell-xs" | "cell-y-md" | "cell-y-sm" | "head-cell-y-md";

export interface TableProps extends React.HTMLAttributes<HTMLDivElement> {
	/**
	 * @default null
	 */
	scrollElement?: null | HTMLElement;

	/**
	 * @default "sm"
	 */
	text?: TableText;

	/**
	 * @default "base-200"
	 */
	theme?: TableTheme;

	/**
	 * @default ["row-b"]
	 */

	borders?: ReadonlyArray<TableBorder>;

	/**
	 * @default ["cell-y-md"]
	 */
	paddings?: ReadonlyArray<TablePadding>;
}

export const Table = React.forwardRef<HTMLDivElement, TableProps>(
	(
		{
			className,
			theme = "base-200",
			text = "sm",
			borders = ["row-b"],
			paddings = ["cell-y-md"],
			scrollElement = null,
			children,
			...props
		},
		ref,
	) => {
		const { sortedBy, virtualRowsState, overflowState } = usePermanent(() => ({
			sortedBy: Signal(ImmutableSet.empty),

			virtualRowsState: Signal(ImmutableMap.empty<React.Key, unknown>()),

			overflowState: Signal<undefined | TableOverflowState>(undefined, {
				equals: isShallowEqual,
			}),
		}));

		const context = React.useMemo(
			(): TableContextValue => ({
				sortedBy,
				virtualRowsState,
				overflowState,
				scrollElement,
			}),
			[sortedBy, virtualRowsState, overflowState, scrollElement],
		);

		useOn(
			"scroll",
			(node) => {
				overflowState.write(readTableOverflowState(node));
			},
			{
				node: scrollElement,
				enabled: scrollElement != null,
				passive: true,
			},
		);

		useOnResize(
			(node) => {
				overflowState.write(readTableOverflowState(node));
			},
			{
				node: scrollElement,
				enabled: scrollElement != null,
			},
		);

		return (
			<TableContextProvider value={context}>
				<div
					className={variants({
						className,
						theme,
						text,
						"border[row-b]": borders.includes("row-b"),
						"border[row-t]": borders.includes("row-t"),
						"padding[cell-xs]": paddings.includes("cell-xs"),
						"padding[cell-y-md]": paddings.includes("cell-y-md"),
						"padding[cell-y-sm]": paddings.includes("cell-y-sm"),
						"padding[head-cell-y-md]": paddings.includes("head-cell-y-md"),
					})}
					ref={ref}
					role="table"
					{...props}
				>
					{children}

					<TableScrollIndicator overflowState={overflowState} />
				</div>
			</TableContextProvider>
		);
	},
);
